import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { supabase } from '@/lib/supabase';
import { useTenant } from '@/hooks/useTenant';
import { getProductImage } from '@/lib/productMedia';
import { isDigitalProduct } from '@/lib/productType';
import { useCart } from '@/hooks/useCart';
import { useDisplayCurrency } from '@/hooks/useDisplayCurrency';
import { normalizeCurrency } from '@/lib/currency';
import { useHeroBanners } from '@/hooks/useHeroBanners';
import { heroBannerToItem, isBannerVisible, buildDefaultHeroBanner } from '@/lib/heroBanners';
import HeroBannerSlider from '@/components/feature/HeroBannerSlider';
import { htmlToText, resolveStoreName } from '@/lib/display';
import { useHomeConfig } from '@/hooks/useHomeConfig';
import { normalizeBlockConfig, orderedEnabledBlocks, productsGridClass } from '@/lib/homeBlocks';
import { AboutSection } from './HomeSections';
import { CtaSection, BookingCalendarSection } from './HomeExtraSections';
import ZifekBlogSection from '@/components/feature/ZifekBlogSection';
import ThemeExtraBlocksRenderer from '@/components/feature/ThemeExtraBlocksRenderer';
import HomeEventsSection from './HomeEventsSection';

// ─────────────────────────────────────────────────────────────
// Page d'accueil publique — type de compte « Boutique »
// Ordre et affichage des blocs pilotés par la config du marchand :
//   Bannière → Produits en vedette → Nos produits (grille 3×3)
// ─────────────────────────────────────────────────────────────

interface BoutiqueProduct {
  id: number;
  name: string;
  description: string | null;
  price: number;
  discount_enabled: boolean;
  discount_price: number | null;
  currency: string | null;
  media: unknown;
  stock: number | null;
  product_type: string | null;
  digital_file_url: string | null;
  created_at: string;
}

const GRID_LIMIT = 9;

export default function BoutiqueHome() {
  const { tenant, theme, isTenant } = useTenant();
  const merchantId = isTenant && tenant ? Number(tenant.id) : null;
  const { banners } = useHeroBanners(merchantId);
  const { config, loading: configLoading } = useHomeConfig(merchantId);
  const { addToCart } = useCart();
  const monaie = normalizeCurrency(tenant?.monaie || 'MAD');
  const { display, original } = useDisplayCurrency(monaie);
  const [featured, setFeatured] = useState<BoutiqueProduct[]>([]);
  const [products, setProducts] = useState<BoutiqueProduct[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!merchantId) return;
    let cancelled = false;
    (async () => {
      try {
        const [featRes, gridRes] = await Promise.all([
          supabase
            .from('product_items')
            .select('*')
            .eq('idcommerce', merchantId)
            .eq('status', 'active')
            .eq('is_featured', true)
            .order('created_at', { ascending: false })
            .limit(8),
          supabase
            .from('product_items')
            .select('*')
            .eq('idcommerce', merchantId)
            .eq('status', 'active')
            .order('created_at', { ascending: false })
            .limit(GRID_LIMIT),
        ]);
        if (cancelled) return;
        setFeatured((featRes.data as BoutiqueProduct[]) || []);
        setProducts((gridRes.data as BoutiqueProduct[]) || []);
      } catch {
        if (!cancelled) {
          setFeatured([]);
          setProducts([]);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [merchantId]);

  const storeName = resolveStoreName(tenant, theme);
  const heroItems = banners.filter(isBannerVisible).map(heroBannerToItem);
  const defaultHero = heroItems.length === 0
    ? buildDefaultHeroBanner({ image: tenant?.imageaboutus, title: storeName, description: tenant?.description || tenant?.aboutus })
    : null;
  const heroDisplay = defaultHero ? [defaultHero] : heroItems;
  const storeDescription = htmlToText(tenant?.description || tenant?.aboutus);
  const aboutTitle = config.aboutTitle.trim() || 'À propos';
  const aboutText = config.aboutContent.trim() || storeDescription;

  const handleAdd = (product: BoutiqueProduct) => {
    const price = Number(product.price || 0);
    const promo = product.discount_enabled && product.discount_price != null;
    const effective = promo ? Number(product.discount_price) : price;
    const digital = isDigitalProduct(product);
    addToCart({
      id: product.id,
      name: product.name,
      price: effective,
      original_price: promo ? price : null,
      media: getProductImage(product.media) || '',
      stock: digital ? 9999 : (product.stock ?? 9999),
      product_type: product.product_type || 'physical',
      digital_file_url: product.digital_file_url,
    });
  };

  const renderCard = (product: BoutiqueProduct, badge?: string) => {
    const imgSrc = getProductImage(product.media);
    const price = Number(product.price || 0);
    const promo = product.discount_enabled && product.discount_price != null;
    const eff = promo && product.discount_price != null ? Number(product.discount_price) : price;
    const digital = isDigitalProduct(product);
    const cur = normalizeCurrency(product.currency || monaie);
    const outOfStock = !digital && product.stock != null && product.stock <= 0;
    return (
      <div
        key={product.id}
        data-product-shop="true"
        className="group relative flex flex-col overflow-hidden rounded-xl border border-background-200/70 bg-background-50 transition-all duration-300 hover:-translate-y-0.5 hover:border-primary-300/80"
      >
        <Link to={`/product/${product.id}`} className="relative block aspect-[4/3] bg-background-100 overflow-hidden no-underline">
          {imgSrc ? (
            <img
              src={imgSrc}
              alt={product.name}
              title={`${product.name} — produit`}
              className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-[1.06]"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center">
              <i className="ri-shopping-bag-line text-3xl text-foreground-300"></i>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-foreground-950/70 via-foreground-950/5 to-transparent"></div>
          <div className="absolute top-2.5 left-2.5 flex flex-col items-start gap-1">
            {badge && (
              <span className="px-2 py-0.5 bg-accent-500 text-background-50 rounded-full text-[10px] font-bold flex items-center gap-1">
                <i className="ri-star-fill text-[9px]"></i>
                {badge}
              </span>
            )}
            {promo && (
              <span className="px-2 py-0.5 bg-primary-500 text-background-50 rounded-full text-[10px] font-bold">
                Promo
              </span>
            )}
          </div>
          {digital && (
            <span className="absolute top-2.5 right-2.5 w-7 h-7 rounded-full bg-background-50/90 text-secondary-700 flex items-center justify-center">
              <i className="ri-download-cloud-2-line text-xs"></i>
            </span>
          )}
          <span className="absolute bottom-2.5 left-2.5 flex items-baseline gap-1.5 rounded-full bg-background-50/95 px-2.5 py-1">
            {promo && product.discount_price != null ? (
              <>
                <span className="text-xs md:text-sm font-bold text-primary-700">{display(Number(product.discount_price), cur)}</span>
                <span className="text-[10px] text-foreground-400 line-through">{display(price, cur)}</span>
              </>
            ) : (
              <span className="text-xs md:text-sm font-bold text-primary-700">{price ? display(price, cur) : 'Gratuit'}</span>
            )}
          </span>
        </Link>
        <div className="flex flex-col flex-1 p-3.5 md:p-4">
          <Link to={`/product/${product.id}`} className="no-underline">
            <h3 className="text-sm font-semibold text-foreground-900 line-clamp-2 group-hover:text-primary-700 transition-colors">{product.name}</h3>
          </Link>
          {price > 0 && original(eff, cur) && (
            <span className="block mt-1 text-[10px] text-foreground-400">{original(eff, cur)}</span>
          )}
          <button
            type="button"
            onClick={() => handleAdd(product)}
            disabled={outOfStock}
            className={`mt-3 w-full flex items-center justify-center gap-1.5 py-2 rounded-lg text-[11px] md:text-xs font-semibold transition-colors cursor-pointer whitespace-nowrap ${
              outOfStock
                ? 'bg-background-100 text-foreground-400 cursor-not-allowed'
                : 'bg-primary-50 text-primary-700 hover:bg-primary-500 hover:text-background-50'
            }`}
          >
            <i className={digital ? 'ri-download-2-line' : 'ri-shopping-cart-line'}></i>
            {outOfStock ? 'Rupture de stock' : digital ? 'Acheter' : 'Ajouter au panier'}
          </button>
        </div>
      </div>
    );
  };

  if (loading || configLoading) {
    return (
      <div className="min-h-screen bg-background-50 flex items-center justify-center py-20">
        <i className="ri-loader-4-line animate-spin text-2xl text-primary-500"></i>
      </div>
    );
  }

  const blocks = normalizeBlockConfig(config.blocks);
  const order = orderedEnabledBlocks(blocks, 'products');

  const bannerSection = heroDisplay.length > 0 ? (
    <HeroBannerSlider key="banner" banners={heroDisplay} />
  ) : (
    <section key="banner" className="relative py-16 md:py-24">
      <div className="absolute inset-0 bg-gradient-to-b from-background-100/60 to-transparent"></div>
      <div className="relative w-full px-4 md:px-6 max-w-7xl mx-auto text-center">
        <h1 className="text-3xl md:text-5xl font-bold font-heading text-foreground-950 mb-3">{storeName}</h1>
        {storeDescription && (
          <p className="text-sm md:text-base text-foreground-500 max-w-2xl mx-auto leading-relaxed">{storeDescription}</p>
        )}
      </div>
    </section>
  );

  const featuredSection = featured.length > 0 ? (
    <section key="featured_products" className="py-10 md:py-16">
      <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center">
            <i className="ri-star-fill mr-2 text-accent-500"></i>
            Produits en vedette
          </h2>
        </div>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 md:gap-5">
          {featured.slice(0, 8).map((p) => renderCard(p, 'Vedette'))}
        </div>
      </div>
    </section>
  ) : null;

  const productsSection = products.length > 0 ? (
    <section key="products" className={`py-10 md:py-16 ${featured.length > 0 ? 'bg-background-100/50' : ''}`}>
      <div className="w-full px-4 md:px-6 max-w-7xl mx-auto">
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl md:text-2xl font-bold font-heading text-foreground-950 flex items-center">
            <i className="ri-shopping-bag-3-line mr-2 text-primary-500"></i>
            Nos produits
          </h2>
          <Link to="/products" className="text-sm text-primary-600 hover:text-primary-700 font-medium cursor-pointer no-underline whitespace-nowrap">
            Voir plus <i className="ri-arrow-right-line ml-1"></i>
          </Link>
        </div>
        <div className={productsGridClass(config.productsColumns)}>
          {products.map((p) => renderCard(p))}
        </div>
        <div className="mt-8 flex justify-center">
          <Link
            to="/products"
            className="inline-flex items-center gap-2 px-6 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold no-underline whitespace-nowrap hover:bg-primary-600 transition-colors cursor-pointer"
          >
            Voir plus de produits
            <i className="ri-arrow-right-line"></i>
          </Link>
        </div>
      </div>
    </section>
  ) : null;

  const aboutSection = aboutText ? (
    <AboutSection key="about" title={aboutTitle} content={aboutText} />
  ) : null;

  const ctaSection = config.ctaTitle.trim() || config.ctaText.trim() ? (
    <CtaSection
      key="cta"
      title={config.ctaTitle}
      text={config.ctaText}
      buttonLabel={config.ctaButtonLabel}
      url={config.ctaUrl}
    />
  ) : null;

  const bookingSection = (
    <BookingCalendarSection key="booking_calendar" serviceId={config.defaultBookingServiceId} />
  );

  const sectionByKey: Record<string, ReactNode> = {
    banner: bannerSection,
    featured_products: featuredSection,
    products: productsSection,
    about: aboutSection,
    cta: ctaSection,
    booking_calendar: bookingSection,
  };

  const rendered = order.map((key) => sectionByKey[key]).filter(Boolean);

  if (rendered.length === 0) {
    return (
      <section className="min-h-screen bg-background-50 py-20">
        <div className="flex flex-col items-center justify-center text-center px-4">
          <div className="w-16 h-16 rounded-full bg-background-100 flex items-center justify-center mb-4">
            <i className="ri-shopping-bag-line text-2xl text-foreground-400"></i>
          </div>
          <p className="text-foreground-500">Aucun produit pour le moment</p>
        </div>
      </section>
    );
  }

  return (
    <div className="min-h-screen bg-background-50">
      {rendered}
      <HomeEventsSection />
      <ThemeExtraBlocksRenderer />
      <ZifekBlogSection />
    </div>
  );
}